import { create } from 'zustand';
import { AuthState, Account, LoginCredentials } from '@appTypes/auth.types';
import { AuthService } from '@services/auth.service';
import { StorageService } from '@utils/storage';

/**
 * Auth store using session-based authentication
 * Matches Angular's principal.service.ts pattern
 */
export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isIdentityResolved: false,
    error: null,
    rememberMe: false,

    /**
     * Login with credentials
     * Returns true on success, false on failure
     */
    login: async (credentials: LoginCredentials): Promise<boolean> => {
        try {
            set({ isLoading: true, error: null });

            // Authenticate with server (session-based)
            await AuthService.login(credentials);

            // Fetch user identity
            const user = await AuthService.getAccount();

            // Store subscriberId for API requests (matches Angular behavior)
            if (user.subscriberId) {
                StorageService.storeSubscriberId(user.subscriberId, credentials.rememberMe);
            }

            set({
                user,
                isAuthenticated: true,
                isLoading: false,
                isIdentityResolved: true,
                rememberMe: credentials.rememberMe,
            });

            return true;
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to sign in. Please check your credentials.';

            set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isIdentityResolved: true,
                error: message,
            });

            return false;
        }
    },

    /**
     * Logout from server and clear state
     */
    logout: async (): Promise<void> => {
        set({ isLoading: true });

        await AuthService.logout();

        set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isIdentityResolved: true,
            error: null,
            rememberMe: false,
        });
    },

    /**
     * Fetch current identity from server
     * If force=true, always fetches from server
     * Otherwise, returns cached user if available
     * Matches Angular's principal.service.ts identity() method
     */
    fetchIdentity: async (force = false): Promise<Account | null> => {
        const state = get();

        // Return cached user if not forcing refresh and already resolved
        if (!force && state.isIdentityResolved && state.user) {
            return state.user;
        }

        try {
            set({ isLoading: true });

            const user = await AuthService.getAccount();

            // Store subscriberId only if none already exists (preserves admin subscriber switch)
            if (user.subscriberId && !StorageService.getSubscriberId()) {
                StorageService.storeSubscriberId(user.subscriberId);
            }

            set({
                user,
                isAuthenticated: true,
                isLoading: false,
                isIdentityResolved: true,
                error: null,
            });

            return user;
        } catch {
            // Not authenticated or session expired
            set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isIdentityResolved: true,
                error: null,
            });

            return null;
        }
    },

    /**
     * Check if user has a specific authority
     * Matches Angular's principal.service.ts hasAuthority() method
     */
    hasAuthority: (authority: string): boolean => {
        const { user, isAuthenticated } = get();
        if (!isAuthenticated || !user) {
            return false;
        }
        return user.authorities.includes(authority);
    },

    /**
     * Check if user has any of the specified authorities
     * Matches Angular's principal.service.ts hasAnyAuthorityDirect() method
     */
    hasAnyAuthority: (authorities: string[]): boolean => {
        const { user, isAuthenticated } = get();
        if (!isAuthenticated || !user) {
            return false;
        }
        if (!authorities || authorities.length === 0) {
            return true; // No authorities required
        }
        return authorities.some((auth) => user.authorities.includes(auth));
    },

    /**
     * Clear error state
     */
    clearError: (): void => {
        set({ error: null });
    },

    /**
     * Set remember me preference
     */
    setRememberMe: (value: boolean): void => {
        set({ rememberMe: value });
    },
}));

export default useAuthStore;
